import { useMemo } from "react";

import {
    LendingVaultExpanded,
    ParsedMarketInformationWithLtvInfo,
    serializeStrategyDuration,
    useStrategyDurations
} from "@bridgesplit/abf-react";
import { StrategyDuration, TokenListMetadata } from "@bridgesplit/abf-sdk";
import { DISABLED_APY, EMPTY_STRING, percentDecimalsToUi } from "@bridgesplit/utils";

import { CollateralAmountsForm, CollateralForm, CollateralFormTableInfo, RateAmountForm } from "./type";

/** Extracts collateral state for update calculation. Same logic as CollateralEditCta. */
export function extractCollateralState(
    vaultExpanded: LendingVaultExpanded | undefined,
    collateralForm: CollateralForm
) {
    const previousCollateralMints = Object.keys(vaultExpanded?.ltvInfos ?? {});
    const currentMintKeys = Object.keys(collateralForm.mintToInfo);

    const newCollateralMints = currentMintKeys.filter((mint) => !previousCollateralMints.includes(mint));
    const allMints = [...previousCollateralMints, ...newCollateralMints];
    const missingCollateral = allMints.filter(
        (mint) => previousCollateralMints.includes(mint) && !currentMintKeys.includes(mint)
    );
    const upcomingCollateral = currentMintKeys.filter((mint) => !missingCollateral.includes(mint));

    return {
        previousCollateralMints,
        newCollateralMints,
        missingCollateral,
        upcomingCollateral
    };
}

export function convertVaultExpandedToMintToRateInfo(
    vaultExpanded: LendingVaultExpanded | undefined,
    marketInfo: ParsedMarketInformationWithLtvInfo | undefined,
    strategyDurations: StrategyDuration[] | undefined
): Record<string, Record<string, RateAmountForm>> {
    const tokenMap: Record<string, Record<string, RateAmountForm>> = {};

    for (const term of Object.entries(vaultExpanded?.vaultStrategy?.terms.assetTerms ?? {})) {
        const [mint, termInfo] = term;
        tokenMap[mint] = {};

        for (const [strategyDuration, apy] of termInfo.durationAndApys) {
            const duration = serializeStrategyDuration(strategyDuration);

            tokenMap[mint][duration] = {
                apy: percentDecimalsToUi(apy).toString()
            };
        }
    }

    for (const asset of marketInfo?.marketInformationAssetInfo ?? []) {
        const mint = asset.assetIdentifier;
        if (!tokenMap[mint]) {
            tokenMap[mint] = {};
            // For mints not in tokenMap, set all strategy durations to DISABLED_APY
            strategyDurations?.forEach((duration) => {
                const serializedDuration = serializeStrategyDuration(duration);
                tokenMap[mint][serializedDuration] = {
                    apy: EMPTY_STRING
                };
            });
        } else {
            strategyDurations?.forEach((duration) => {
                const serializedDuration = serializeStrategyDuration(duration);
                if (!tokenMap[mint][serializedDuration]) {
                    tokenMap[mint][serializedDuration] = { apy: EMPTY_STRING };
                }
            });
        }
    }

    return tokenMap;
}
export function addMetadataBasedOnCollateral(
    collateralMintToInfo: Record<string, CollateralAmountsForm>,
    getMetadata: (mint: string | undefined | null) => TokenListMetadata | undefined
): CollateralFormTableInfo[] {
    return Object.entries(collateralMintToInfo).map(([mint, value], index) => ({
        ...value,
        id: index,
        metadata: getMetadata(mint)
    }));
}

// Fetches all strategy durations and populates the apy with presets for a given collateral.
// Returns a record of strategy duration to apy.
export function useDefaultDurationsAndApys(): { data: Record<string, string>; isLoading: boolean } {
    const { strategyDurations, isLoading } = useStrategyDurations();

    const durationsAndApys = useMemo(() => {
        const result: Record<string, string> = {};
        if (!strategyDurations) return result;
        strategyDurations.forEach((duration) => {
            result[serializeStrategyDuration(duration)] = DISABLED_APY; // Set default value to 0
        });
        return result;
    }, [strategyDurations]);

    return { data: durationsAndApys, isLoading };
}

/**
 * Returns true if the collateral form would produce any strategy updates (additions, removals, or updates).
 * Same logic as CollateralEditCta - compare derived updates, not form directly.
 */
// Collateral form is "same" if it matches the original market info fields for all fields mapped in updateCollateralForm
export function isCollateralChange(
    form: CollateralForm,
    marketInfo: ParsedMarketInformationWithLtvInfo | undefined
): boolean {
    // If there is no market info to compare against, form is not changeable
    if (!marketInfo) return false;

    const reference: Record<string, CollateralAmountsForm> = {};
    for (const asset of marketInfo.marketInformationAssetInfo) {
        reference[asset.assetIdentifier] = {
            ltvUiAmount: percentDecimalsToUi(asset.ltv),
            liquidationLtvAmount: percentDecimalsToUi(asset.liquidationThreshold),
            marketInformationAddress: marketInfo.marketInformation.address,
            oracleAddress: asset.oracleAccount,
            oracleType: asset.oracleType
        };
    }

    const formMints = Object.keys(form.mintToInfo);
    const referenceMints = Object.keys(reference);

    if (formMints.length !== referenceMints.length) {
        return true;
    }

    for (const mint of referenceMints) {
        const formInfo = form.mintToInfo[mint];
        const referenceInfo = reference[mint];

        if (!formInfo) {
            return true; // missing in form = changed
        }

        // Compare each key defined in reference
        for (const key of Object.keys(referenceInfo) as (keyof CollateralAmountsForm)[]) {
            if (formInfo[key] !== referenceInfo[key]) {
                return true;
            }
        }
    }

    return false;
}
